import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, ShoppingCart, MapPin, Package, Calendar, Phone, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { productsAPI, categoriesAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../lib/utils';
import { getPackageLabel, getPackageDisplay, getPricePerUnit } from '../lib/packageUtils';
import { toast } from 'sonner';

const MONTHS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const formatSeason = (start, end) => {
    if (!start || !end) return null;
    return `${MONTHS[start - 1]} a ${MONTHS[end - 1]}`;
};

const isInSeason = (start, end) => {
    if (!start || !end) return null;
    const currentMonth = new Date().getMonth() + 1;
    if (start <= end) {
        return currentMonth >= start && currentMonth <= end;
    } else {
        return currentMonth >= start || currentMonth <= end;
    }
};

export const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const { isAuthenticated } = useAuth();
    const [product, setProduct] = useState(null);
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        const loadProduct = async () => {
            try {
                const res = await productsAPI.getById(id);
                setProduct(res.data);
                setQuantity(res.data.min_order || 1);
                const catRes = await categoriesAPI.getAll();
                const cats = catRes.data.categories || catRes.data || [];
                const cat = cats.find(c => c.id === res.data.category_id);
                setCategory(cat);
            } catch {
                navigate('/catalogue');
            } finally {
                setLoading(false);
            }
        };
        loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on id change only
    }, [id, navigate]);

    const isOrderable = product?.ready_to_order && product?.price > 0;

    const handleAddToCart = () => {
        if (!isAuthenticated) {
            toast.error('Connectez-vous pour commander');
            navigate('/connexion');
            return;
        }
        addItem(product, quantity);
        toast.success(
            isOrderable
                ? `${product.name} ajoute a la commande`
                : `${product.name} ajoute a la demande de prix`
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    if (!product) return null;

    return (
        <div className="min-h-screen bg-background" data-testid="product-detail-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Retour
                    </Button>
                    <span>/</span>
                    <Link to="/catalogue" className="hover:text-foreground">Catalogue</Link>
                    {category && (
                        <>
                            <span>/</span>
                            <Link to={`/catalogue?category=${category.id}`} className="hover:text-foreground">
                                {category.name}
                            </Link>
                        </>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Image */}
                    <div className="aspect-square rounded-xl overflow-hidden bg-muted">
                        <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            data-testid="product-image"
                        />
                    </div>

                    {/* Details */}
                    <div>
                        {category && (
                            <Badge 
                                className="mb-4" 
                                style={{ backgroundColor: category.color + '20', color: category.color }}
                            >
                                {category.name}
                            </Badge>
                        )}
                        
                        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight" data-testid="product-name">
                            {product.name}
                        </h1>
                        
                        <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
                            {product.description}
                        </p>

                        <Separator className="my-6" />

                        {/* Price */}
                        {isOrderable ? (
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-leaf/10 border border-leaf/20">
                                <div>
                                    <p className="text-3xl font-bold text-leaf" data-testid="product-price">
                                        {formatPrice(product.price)}
                                        <span className="text-base font-normal text-muted-foreground ml-2">/ {product.unit}</span>
                                    </p>
                                    <p className="text-xs text-leaf/70 mt-1">Pret a commander</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                                <Phone className="h-5 w-5 text-amber-600" />
                                <div>
                                    <p className="font-semibold text-amber-800" data-testid="product-price">Prix sur demande</p>
                                    <p className="text-xs text-amber-600">Contactez-nous pour obtenir nos meilleurs tarifs</p>
                                </div>
                            </div>
                        )}

                        {/* Info */}
                        <div className="mt-6 space-y-3">
                            {product.gen_code && (
                                <div className="flex items-center gap-2 text-sm" data-testid="product-gen-code">
                                    <Package className="h-4 w-4 text-muted-foreground" />
                                    <span>GENCOD: <strong className="font-mono">{product.gen_code}</strong></span>
                                </div>
                            )}
                            {product.origin && (
                                <div className="flex items-center gap-2 text-sm">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    <span>Origine: <strong>{product.origin}</strong></span>
                                </div>
                            )}
                            {product.package_weight > 0 && (
                                <div className="flex items-center gap-2 text-sm" data-testid="product-package-weight">
                                    <Package className="h-4 w-4 text-amber-500" />
                                    <span>{getPackageLabel(product.unit)} : <strong>{getPackageDisplay(product.package_weight, product.unit)}</strong></span>
                                    <span className="text-xs text-muted-foreground">(Total = {formatPrice(product.price)}{getPricePerUnit(product.unit)} x {getPackageDisplay(product.package_weight, product.unit)} x quantité)</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-sm">
                                <Package className="h-4 w-4 text-muted-foreground" />
                                <span>Commande minimum: <strong>{product.min_order} colis</strong></span>
                            </div>
                            {product.season_start && product.season_end && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span>Saison: <strong>{formatSeason(product.season_start, product.season_end)}</strong></span>
                                    {isInSeason(product.season_start, product.season_end) ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">En saison</Badge>
                                    ) : (
                                        <Badge className="bg-orange-100 text-orange-700 text-xs">Hors saison</Badge>
                                    )}
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-sm">
                                <div className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-leaf' : 'bg-destructive'}`} />
                                <span>{product.stock > 0 ? `En stock (${product.stock} disponibles)` : 'Rupture de stock'}</span>
                            </div>
                        </div>

                        <Separator className="my-6" />

                        {/* Add to Cart */}
                        {isAuthenticated ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <span className="text-sm font-medium">Quantite:</span>
                                    <div className="flex items-center border rounded-lg">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setQuantity(Math.max(product.min_order, quantity - 1))}
                                            data-testid="decrease-quantity"
                                        >
                                            <Minus className="h-4 w-4" />
                                        </Button>
                                        <span className="w-16 text-center font-medium">{quantity}</span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setQuantity(quantity + 1)}
                                            data-testid="increase-quantity"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <span className="text-sm text-muted-foreground">{product.unit}</span>
                                </div>

                                <Button 
                                    onClick={handleAddToCart} 
                                    size="lg" 
                                    className={`w-full h-12 ${
                                        isOrderable
                                            ? 'bg-jungle hover:bg-jungle/90 text-sand'
                                            : 'bg-amber-600 hover:bg-amber-700 text-white'
                                    }`}
                                    disabled={product.stock === 0}
                                    data-testid="add-to-cart-button"
                                >
                                    {isOrderable ? (
                                        <>
                                            <ShoppingCart className="h-5 w-5 mr-2" />
                                            Ajouter a la commande
                                        </>
                                    ) : (
                                        <>
                                            <FileText className="h-5 w-5 mr-2" />
                                            Demander un prix
                                        </>
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <div className="bg-muted rounded-lg p-6 text-center">
                                <p className="text-muted-foreground mb-4">
                                    Connectez-vous pour commander ce produit
                                </p>
                                <Button asChild className="bg-leaf hover:bg-leaf/90" data-testid="login-to-order">
                                    <Link to="/connexion">Se connecter</Link>
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
